/**
 * API Service - Axios instance with interceptors
 * Handles all HTTP communications with the backend
 * Features: Token refresh, error handling, type safety, environment auto-detection
 *
 * ⭐ HTTP CLIENT STANDARD - SOURCE OF TRUTH ⭐
 * ============================================
 * THIS IS THE OFFICIAL HTTP CLIENT FOR ALL SERVICES
 *
 * All other services MUST use apiClient exported from this file.
 * DO NOT create custom HTTP clients or use raw axios/fetch elsewhere.
 *
 * Features provided by apiClient:
 * - Automatic CSRF token injection via request interceptors
 * - Automatic retry on 403 CSRF errors
 * - Centralized error handling and logging
 * - Type-safe responses with TypeScript
 * - Session cookie management (withCredentials: true)
 * - Environment-aware base URL configuration
 * - Development/production logging
 *
 * See: /HTTP_CLIENT_STANDARDS.md for complete documentation
 *
 * Last Updated: 2025-10-09
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { ENV } from '../config/env';
import { createRequestTracker } from '../utils/errorDebugger';
import { getCsrfToken as getCsrfTokenFromUtil, refreshCsrfToken } from '../utils/csrf';
import {
  createRequestTransformInterceptor,
  createResponseTransformInterceptor
} from '../utils/caseTransform';

// API Configuration - Auto-detects production vs local
const API_BASE_URL = ENV.apiUrl;
const API_TIMEOUT = 30000; // 30 seconds

// Log API configuration
console.log('[API Config] Environment:', ENV.environment);
console.log('[API Config] Base URL:', API_BASE_URL);
console.log('[API Config] Hostname:', window.location.hostname);
console.log('[API Config] Backend URL:', ENV.backendUrl);

// Error response type
export interface ApiError {
  message: string;
  status?: number;
  errors?: Record<string, string[]>;
  detail?: string;
}

// Generic API response type
export interface ApiResponse<T = unknown> {
  data: T;
  message?: string;
  status: number;
}

/**
 * Create axios instance with default config
 */
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    timeout: API_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true, // Important for httpOnly cookies
  });

  return instance;
};

// Create the main API instance
export const apiClient: AxiosInstance = createAxiosInstance();

// Create request tracker for enhanced debugging
const requestTracker = createRequestTracker();

/**
 * Case Transformation Interceptors
 * Automatically convert between camelCase (frontend) and snake_case (backend)
 */
// Request: camelCase → snake_case
apiClient.interceptors.request.use(createRequestTransformInterceptor());

// Response: snake_case → camelCase
apiClient.interceptors.response.use(createResponseTransformInterceptor());

/**
 * Request interceptor - Add auth headers and handle request config
 */
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {


    // Add CSRF token for state-changing requests (POST, PUT, PATCH, DELETE)
    const isStateChangingRequest = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(
      config.method?.toUpperCase() || ''
    );

    if (isStateChangingRequest) {
      try {
        const csrfToken = await getCsrfTokenFromUtil();
        if (csrfToken && config.headers) {
          config.headers['X-CSRF-Token'] = csrfToken;
        }
      } catch (error) {
        console.error('[CSRF Token Error]', error);
        // Continue with request even if CSRF token fails
        // Backend will reject it, but this prevents blocking the UI
      }
    }



    // Add request tracking
    requestTracker.onRequest(config);
    return config;
  },
  (error: AxiosError) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

/**
 * Response interceptor - Handle responses and errors
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {


    // Track successful response
    requestTracker.onResponse(response);

    return response;
  },
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized - Token expired
    // Skip refresh attempt for auth endpoints to avoid infinite loops
    const isAuthEndpoint = originalRequest?.url?.includes('/auth/session') ||
                          originalRequest?.url?.includes('/auth/refresh') ||
                          originalRequest?.url?.includes('/auth/login') ||
                          originalRequest?.url?.includes('/auth/logout');

    // Don't log expected 401s on auth endpoints (session checks when not logged in)
    const isExpectedAuthError = error.response?.status === 401 && isAuthEndpoint;

    // Log errors in development
    if (import.meta.env.DEV) {
      if (isExpectedAuthError) {
        // Use debug level for expected auth errors (session checks when not logged in)
        console.debug('[API Response - Expected]', {
          url: error.config?.url,
          status: error.response?.status,
          message: 'Session check - not authenticated (expected)',
        });
      } else {
        // Use error level for unexpected errors
        console.error('[API Response Error]', {
          url: error.config?.url,
          status: error.response?.status,
          message: error.response?.data?.message || error.message,
        });
      }
    }

    // Track error (skip expected auth errors)
    if (!isExpectedAuthError) {
      await requestTracker.onError(error);
    }

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      try {
        console.log('[AUTH] Attempting session refresh due to 401...');
        // Try to refresh the session
        await apiClient.post('/api/v1/auth/refresh');

        console.log('[AUTH] Session refresh successful, retrying original request');
        // Retry the original request
        return apiClient(originalRequest);
      } catch (refreshError) {
        console.error('[AUTH] Session refresh failed:', refreshError);

        // DON'T do hard redirect - let AuthProvider handle it
        // The AuthProvider's retry logic will handle this more gracefully
        console.log('[AUTH] Letting AuthProvider handle session expiration');
        return Promise.reject(refreshError);
      }
    }

    // Handle 403 Forbidden - CSRF token issue
    if (error.response?.status === 403 && originalRequest && !originalRequest._csrfRetry) {
      const errorData = error.response.data as any;
      const errorCode = errorData?.error?.code;
      const errorMessage = errorData?.error?.message || errorData?.message || errorData?.detail;

      // Check if it's a CSRF error
      const isCsrfError = errorCode?.includes('CSRF') || errorMessage?.toLowerCase().includes('csrf');

      if (isCsrfError) {
        originalRequest._csrfRetry = true;

        try {
          // Refresh CSRF token using our utility
          await refreshCsrfToken();
          console.log('[CSRF] Token refreshed after 403, retrying request...');

          // Retry the original request with new token
          return apiClient(originalRequest);
        } catch (csrfError) {
          console.error('[CSRF Refresh Error]', csrfError);
          // Let the error propagate
        }
      }
    }

    // Return formatted error
    return Promise.reject(formatApiError(error));
  }
);

/**
 * Format API error for consistent error handling
 */
export function formatApiError(error: AxiosError<any>): ApiError {
  if (error.response) {
    // Server responded with error
    const { data, status } = error.response;

    // Extract error message from various formats
    let message = 'An error occurred';
    let detail: string | undefined;
    let errors: Record<string, string[]> | undefined;

    // Handle backend error format: { error: "...", detail: "..." or [...] }
    if (data?.error) {
      message = data.error;
    } else if (data?.message) {
      message = data.message;
    }

    // Handle detail field (can be string or array of validation errors)
    if (data?.detail) {
      if (typeof data.detail === 'string') {
        detail = data.detail;
        // Use detail as message if error/message not present
        if (!data?.error && !data?.message) {
          message = data.detail;
        }
      } else if (Array.isArray(data.detail)) {
        // Validation errors - extract messages
        const validationMessages = data.detail.map((err: any) => {
          const field = err.loc ? err.loc.join('.') : 'field';
          return `${field}: ${err.msg || 'Invalid value'}`;
        });
        detail = validationMessages.join(', ');
        // Use first validation error as main message
        if (validationMessages.length > 0) {
          message = validationMessages[0];
        }
      }
    }

    // Handle errors field
    if (data?.errors) {
      errors = data.errors;
    }

    return {
      message,
      status,
      errors,
      detail,
    };
  } else if (error.request) {
    // Request made but no response
    return {
      message: 'No response from server. Please check your connection.',
      status: 0,
    };
  } else {
    // Error setting up request
    return {
      message: error.message || 'An unexpected error occurred',
    };
  }
}

/**
 * Check if error is an API error
 */
export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as ApiError).message === 'string'
  );
}

/**
 * Extract error message from unknown error
 */
export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred';
}

/**
 * Type-safe API client methods
 */
export const api = {
  /**
   * GET request
   */
  async get<T = unknown>(url: string, params?: Record<string, unknown>): Promise<T> {
    const response = await apiClient.get<T>(url, { params });
    return response.data;
  },

  /**
   * POST request
   */
  async post<T = unknown>(url: string, data?: unknown): Promise<T> {
    const response = await apiClient.post<T>(url, data);
    return response.data;
  },

  /**
   * PUT request
   */
  async put<T = unknown>(url: string, data?: unknown): Promise<T> {
    const response = await apiClient.put<T>(url, data);
    return response.data;
  },

  /**
   * PATCH request
   */
  async patch<T = unknown>(url: string, data?: unknown): Promise<T> {
    const response = await apiClient.patch<T>(url, data);
    return response.data;
  },

  /**
   * DELETE request
   */
  async delete<T = unknown>(url: string): Promise<T> {
    const response = await apiClient.delete<T>(url);
    return response.data;
  },
};

// Extend Axios request config to include retry flags
declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    _retry?: boolean;
    _csrfRetry?: boolean;
  }
}

export default api;
